/**
 * Localization Service - Merges local and Strapi translations
 * Fetches from Strapi, falls back to local en.json, and deep merges both
 */

import axios from 'axios';

const STRAPI_ENDPOINT = process.env.VUE_APP_STRAPI_URL || 'http://localhost:1342';
const LOCAL_TRANSLATIONS = () => require('@/locales/en.json');

/**
 * Deep merge objects - Strapi translations override local defaults
 * @param {Object} local - Local translation object
 * @param {Object} strapi - Strapi translation object
 * @returns {Object} Merged translation object
 */
const deepMerge = (local = {}, strapi = {}) => {
  const result = { ...local };

  for (const key in strapi) {
    if (Object.prototype.hasOwnProperty.call(strapi, key)) {
      if (
        typeof strapi[key] === 'object' &&
        strapi[key] !== null &&
        !Array.isArray(strapi[key]) &&
        typeof result[key] === 'object' &&
        result[key] !== null
      ) {
        // Recursively merge nested objects
        result[key] = deepMerge(result[key], strapi[key]);
      } else {
        // Override with Strapi value
        result[key] = strapi[key];
      }
    }
  }

  return result;
};

/**
 * Parse Strapi response - handle different response structures
 * @param {Object} response - Strapi API response
 * @returns {Object} Parsed translations object
 */
const parseStrapiResponse = response => {
  if (!response) return {};

  // Handle different Strapi response structures
  // Case 1: Direct data object with translations
  if (response.data) {
    const data = Array.isArray(response.data) ? response.data[0] : response.data;

    // Handle attributes nested structure (Strapi v4+)
    if (data?.attributes?.translations) {
      return typeof data.attributes.translations === 'string'
        ? JSON.parse(data.attributes.translations)
        : data.attributes.translations;
    }

    // Handle flat structure
    if (data?.translations) {
      return typeof data.translations === 'string' ? JSON.parse(data.translations) : data.translations;
    }
  }

  return {};
};

/**
 * Fetch and merge translations
 * @returns {Promise<Object>} Merged translations with fallback to local
 */
export const loadLocalization = async () => {
  const localTranslations = LOCAL_TRANSLATIONS();

  try {
    const response = await axios.get(`${STRAPI_ENDPOINT}/api/website-localization`, {
      timeout: 5000, // 5 second timeout
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const strapiTranslations = parseStrapiResponse(response.data);

    // Merge: local is default, Strapi overrides
    return deepMerge(localTranslations, strapiTranslations);
  } catch (error) {
    console.warn('Failed to load Strapi localization, using local defaults:', error.message);
    // Return local translations as fallback
    return localTranslations;
  }
};

/**
 * Refresh localization - call this if you need to reload translations
 * @param {VueI18n} i18n - Vue i18n instance
 * @returns {Promise<Object>} Updated translations
 */
export const refreshLocalization = async i18n => {
  try {
    const translations = await loadLocalization();
    i18n.setLocaleMessage('en', translations);
    return translations;
  } catch (error) {
    console.error('Failed to refresh localization:', error);
    throw error;
  }
};

export default {
  loadLocalization,
  refreshLocalization
};
